import type {
  ChangePasswordRequest,
  UserAccountLevelUpdateRequest,
  UserCreateRequest,
  UserListParams,
  UserUpdateRequest,
} from '@/shared/api/types/user-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  changeUserPassword,
  createUser,
  getCurrentUser,
  getUserById,
  getUsers,
  resetUserPassword,
  toggleUserStatus,
  updateUser,
  updateUserAccountLevel,
} from '@/shared/api/services/user-service'

export const USER_QUERY_KEYS = {
  all: ['users'] as const,
  list: (params: UserListParams) => [...USER_QUERY_KEYS.all, 'list', params] as const,
  detail: (id: string) => [...USER_QUERY_KEYS.all, 'detail', id] as const,
  me: () => [...USER_QUERY_KEYS.all, 'me'] as const,
}

export function useUsersQuery(params: UserListParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: USER_QUERY_KEYS.list(params),
    queryFn: async () => getUsers(params),
    enabled: options.enabled ?? true,
  })
}

export function useUserByIdQuery(id: string) {
  return useQuery({
    queryKey: USER_QUERY_KEYS.detail(id),
    queryFn: async () => getUserById(id),
    enabled: !!id,
  })
}

export function useCurrentUserQuery(enabled = true) {
  return useQuery({
    queryKey: USER_QUERY_KEYS.me(),
    queryFn: async () => getCurrentUser(),
    enabled,
  })
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UserCreateRequest) => createUser(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.all })
    },
  })
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UserUpdateRequest }) =>
      updateUser(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.all })
    },
  })
}

export function useUpdateUserAccountLevelMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UserAccountLevelUpdateRequest }) =>
      updateUserAccountLevel(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.all })
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.detail(id) })
    },
  })
}

export function useChangeUserPasswordMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ChangePasswordRequest }) =>
      changeUserPassword(id, payload),
  })
}

export function useResetUserPasswordMutation() {
  return useMutation({
    mutationFn: async (id: string) => resetUserPassword(id),
  })
}

export function useToggleUserStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => toggleUserStatus(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.all })
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.detail(id) })
    },
  })
}
